import { memo, type CSSProperties, type FC, type ReactNode, type RefObject } from 'react';
import { twMerge } from 'tailwind-merge';

interface Option {
  value: string | number;
  label: ReactNode;
  renderOption?: (option: Option) => ReactNode;
  renderButton?: (option: Option) => ReactNode;
}

interface KawaiiSelectDropdownProps {
  options: Option[];
  value?: string | number;
  opensUpward: boolean;
  focusedIndex: number;
  handleDropdownKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  getOptionRef: (index: number) => (el: HTMLButtonElement | null) => void;
  handleOptionClick: (selectedValue: string | number) => void;
  dropdownRef: RefObject<HTMLDivElement>;
  dropdownStyle: CSSProperties;
  optionClassName?: string;
}

// The dropdown portal content! 뿅!
const KawaiiSelectDropdownComponent: FC<KawaiiSelectDropdownProps> = ({
  options,
  value,
  opensUpward,
  focusedIndex,
  handleDropdownKeyDown,
  getOptionRef,
  handleOptionClick,
  dropdownRef,
  dropdownStyle,
  optionClassName,
}) => {
  return (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className={`
        scrollbar-thin
        fixed z-9999
        bg-white dark:bg-gray-900
        border-2 border-pink-200 dark:border-pink-700
        rounded-lg shadow-black/20 shadow-xl
        outline-hidden
        flex flex-col 
        overflow-y-auto
        pointer-events-auto
        focus:outline-hidden
        ${opensUpward ? 'animate-kawaii-dropdown-up' : 'animate-kawaii-dropdown-down'}
      `}
      role="listbox"
      tabIndex={-1} // Make it focusable for keyboard events
      onKeyDown={handleDropdownKeyDown}
    >
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={getOptionRef(index)} // Use the ref callback!
          type="button"
          role="option"
          aria-selected={option.value === value}
          tabIndex={-1} // Options are not directly tabbable
          onClick={() => handleOptionClick(option.value)}
          className={twMerge(
            `
            w-full px-4 py-1 text-left
            text-gray-700 dark:text-gray-300
            hover:bg-pink-100 dark:hover:bg-gray-800
            focus:bg-pink-100 dark:focus:bg-gray-800 focus:outline-hidden
            transition-colors duration-150
            flex items-center gap-2
            cursor-pointer
            ${index === focusedIndex ? 'bg-pink-100 dark:bg-gray-800' : ''}
          `,
            optionClassName,
          )}
        >
          <span className="flex-1 truncate">
            {option.renderOption ? option.renderOption(option) : option.label}
          </span>
        </button>
      ))}
    </div>
  );
};

KawaiiSelectDropdownComponent.displayName = 'KawaiiSelectDropdown';

export const KawaiiSelectDropdown = memo(KawaiiSelectDropdownComponent);
